import posthog from 'posthog-js';

// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------
export const initPostHog = () => {
  const apiKey = process.env.REACT_APP_POSTHOG_API_KEY;
  const host   = process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com';

  if (!apiKey) {
    console.warn('[PostHog] REACT_APP_POSTHOG_API_KEY is not set. Analytics disabled.');
    return;
  }
  posthog.init(apiKey, {
    api_host: host,
    autocapture: false,
    capture_pageview: true,
    persistence: 'localStorage',
    person_profiles: 'identified_only',
    // NOTE: do NOT opt_out in dev — it silently blocks identify() and profile creation
  });
};

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------
export const identifyUser = (userId, traits = {}) => {
  posthog.identify(userId, traits);
};

export const resetUser = () => {
  posthog.reset();
};

// ---------------------------------------------------------------------------
// Generic capture
// ---------------------------------------------------------------------------
export const captureEvent = (event, properties = {}) => {
  posthog.capture(event, properties);
};

// ===========================================================================
// A. QUERY LIFECYCLE
// ===========================================================================

/**
 * Fired the moment a user submits a query (before any API call).
 * is_refinement=true when this is a follow-up to an existing answer session.
 */
export const trackQuerySubmitted = (query, { searchId, isRefinement = false, previousQuery = null, intentType = null } = {}) =>
  captureEvent('query_submitted', {
    query_text: query,
    search_id: searchId,
    is_refinement: isRefinement,
    previous_query: previousQuery,
    intent_type: intentType,            // e.g. 'lookup' | 'troubleshooting' | 'comparison'
    submitted_at: new Date().toISOString(),
  });

/**
 * Fired when the AI/Saras response has been fully generated (streaming complete).
 */
export const trackResponseGenerated = (query, { searchId, responseTimeMs, sourcesCount = 0, answerType = null, hasAnswer = true } = {}) =>
  captureEvent('response_generated', {
    query_text: query,
    search_id: searchId,
    response_time_ms: responseTimeMs,
    sources_count: sourcesCount,        // number of citations
    answer_type: answerType,            // 'summary' | 'step-by-step' | 'list' | 'explanation'
    has_answer: hasAnswer,
    generated_at: new Date().toISOString(),
  });

/**
 * Fired when the rendered answer is visible to the user.
 */
export const trackResponseDisplayed = (query, { searchId, totalTimeMs } = {}) =>
  captureEvent('response_displayed', {
    query_text: query,
    search_id: searchId,
    total_time_ms: totalTimeMs,
  });

// ===========================================================================
// B. ANSWER QUALITY SIGNALS  (North-star layer)
// ===========================================================================

/**
 * Fired when the user has spent enough time viewing the response (≥3 s visible).
 * view_time_ms = wall-clock milliseconds the answer was in the viewport.
 */
export const trackResponseViewed = (query, { searchId, viewTimeMs, scrollDepthPct = 0 } = {}) =>
  captureEvent('response_viewed', {
    query_text: query,
    search_id: searchId,
    view_time_ms: viewTimeMs,
    scroll_depth_pct: scrollDepthPct,
  });

/** Fired on scroll inside the answer box; scroll_depth_pct 0–100. */
export const trackResponseScrolled = (query, { searchId, scrollDepthPct } = {}) =>
  captureEvent('response_scrolled', {
    query_text: query,
    search_id: searchId,
    scroll_depth_pct: scrollDepthPct,
  });

/** Fired when the user copies text from the answer. */
export const trackResponseCopied = (query, { searchId, copiedLength = null } = {}) =>
  captureEvent('response_copied', {
    query_text: query,
    search_id: searchId,
    copied_length: copiedLength,
  });

/**
 * Fired when the user rates the response (thumbs up/down).
 * rating: 'positive' | 'negative'
 */
export const trackResponseRated = (query, { searchId, rating, reason = null, documentId = null } = {}) =>
  captureEvent('response_rated', {
    query_text: query,
    search_id: searchId,
    rating,                // 'positive' | 'negative'
    reason,
    document_id: documentId,
  });

// ===========================================================================
// C. QUERY REFINEMENT  (Key failure signals)
// ===========================================================================

/**
 * Fired when the user submits a new query while already viewing results —
 * i.e. the first query didn't satisfy them.
 * refinement_type: 'reword' | 'more_detail' | 'shorter' | 'follow_up' | 'unrelated'
 */
export const trackQueryRefined = (newQuery, { previousQuery, searchId, timeToRefineMs = null, refinementType = null } = {}) =>
  captureEvent('query_refined', {
    query_text: newQuery,
    previous_query: previousQuery,
    search_id: searchId,
    time_to_refine_ms: timeToRefineMs,
    refinement_type: refinementType,
  });

/**
 * Fired when a user navigates away without interacting with the result.
 * Indicates the response failed to engage them.
 */
export const trackQueryAbandoned = (query, { searchId, dwellTimeMs, hadAnswer = false } = {}) =>
  captureEvent('query_abandoned', {
    query_text: query,
    search_id: searchId,
    dwell_time_ms: dwellTimeMs,
    had_answer: hadAnswer,
  });

/**
 * Derived success signal: no refinement + no abandonment + sufficient dwell time.
 * Fire this after a configurable quiet period (e.g. 60 s without a new query).
 */
export const trackQuerySuccessInferred = (query, { searchId, dwellTimeMs, sourcesCount = 0 } = {}) =>
  captureEvent('query_success_inferred', {
    query_text: query,
    search_id: searchId,
    dwell_time_ms: dwellTimeMs,
    sources_count: sourcesCount,
  });

/**
 * North-star event: user got the answer without asking any follow-up questions
 * or navigating away. Fires after 60 s of silence (no refinement, no follow-up click,
 * no session exit). The 60 s timer is cancelled automatically when the user refines
 * their query or clicks a follow-up, so if this fires the zero-click condition is met.
 *
 * dwell_time_ms   — how long since the answer was displayed
 * response_time_ms — how long the AI took to generate the answer
 * sources_count    — number of citations in the answer
 */
export const trackZeroClickSuccess = (query, { searchId, dwellTimeMs, responseTimeMs = null, sourcesCount = 0 } = {}) =>
  captureEvent('zero_click_success', {
    query_text: query,
    search_id: searchId,
    dwell_time_ms: dwellTimeMs,
    response_time_ms: responseTimeMs,
    sources_count: sourcesCount,
  });

// ===========================================================================
// D. CITATION TRUST TRACKING
// ===========================================================================

/**
 * Fired when the user clicks a citation link in the AI answer.
 * source_type: 'ingested' | 'authored'
 */
export const trackCitationClicked = (query, { searchId, documentId, citationRank = null, sourceType = null, title = null } = {}) =>
  captureEvent('citation_clicked', {
    query_text: query,
    search_id: searchId,
    document_id: documentId,
    citation_rank: citationRank,
    source_type: sourceType,
    title,
  });

/** Fired when a citation detail panel / section is expanded in the answer. */
export const trackCitationExpanded = (query, { searchId, documentId, sourceType = null } = {}) =>
  captureEvent('citation_expanded', {
    query_text: query,
    search_id: searchId,
    document_id: documentId,
    source_type: sourceType,
  });

// ===========================================================================
// E + F. FAILURE SIGNALS
// ===========================================================================

/** Fired when the AI returns no useful answer. */
export const trackNoResponseGenerated = (query, { searchId, reason = null, responseTimeMs = null } = {}) =>
  captureEvent('no_response_generated', {
    query_text: query,
    search_id: searchId,
    reason,
    response_time_ms: responseTimeMs,
  });

/** Fired when the system falls back to a degraded mode (e.g. streaming error). */
export const trackFallbackTriggered = (query, { searchId, reason = null } = {}) =>
  captureEvent('fallback_triggered', {
    query_text: query,
    search_id: searchId,
    reason,
  });

// ===========================================================================
// G. RETRIEVAL / RAG DEBUG LAYER
// ===========================================================================

/** Fired when the retrieval (search API) call completes. */
export const trackRetrievalPerformed = (query, { searchId, documentsRetrieved, retrievalLatencyMs, indexSource = null } = {}) =>
  captureEvent('retrieval_performed', {
    query_text: query,
    search_id: searchId,
    documents_retrieved: documentsRetrieved,
    retrieval_latency_ms: retrievalLatencyMs,
    index_source: indexSource,
  });

// ===========================================================================
// IDENTITY / SESSION
// ===========================================================================

export const trackLogin = (userId, traits = {}, method = 'keycloak') => {
  identifyUser(userId, traits);
  captureEvent('user_logged_in', { method, user_id: userId, login_at: new Date().toISOString() });
};

export const trackLogout = (sessionDurationSeconds = null) => {
  captureEvent('user_logged_out', {
    logout_at: new Date().toISOString(),
    ...(sessionDurationSeconds !== null && { session_duration_seconds: sessionDurationSeconds }),
  });
  resetUser();
};

export const trackSessionDuration = (durationSeconds) =>
  captureEvent('session_duration', { duration_seconds: durationSeconds });

// ===========================================================================
// NAVIGATION / PAGE VIEWS
// ===========================================================================

export const trackPageView = (path, title = document.title) =>
  captureEvent('$pageview', { $current_url: window.location.href, path, title });

export const trackTimeOnPage = (path, durationSeconds, extraProps = {}) =>
  captureEvent('time_on_page', { path, duration_seconds: durationSeconds, ...extraProps });

// ===========================================================================
// SEARCH RESULT LIST (traditional search result interactions)
// ===========================================================================

export const trackSearchResultsLoaded = (query, resultCount, loadTimeMs, filters = {}) =>
  captureEvent('search_results_loaded', { query_text: query, result_count: resultCount, load_time_ms: loadTimeMs, ...filters });

export const trackSearchResultClick = (query, documentId, title, rank) =>
  captureEvent('search_result_clicked', { query_text: query, document_id: documentId, title, rank });

export const trackPaginationChange = (page, query) =>
  captureEvent('search_pagination_changed', { page, query_text: query });

// ===========================================================================
// AUTOCOMPLETE
// ===========================================================================

export const trackAutocompleteShown = (query, suggestionCount) =>
  captureEvent('autocomplete_shown', { query_text: query, suggestion_count: suggestionCount });

export const trackAutocompleteSuggestionClicked = (query, suggestion, rank) =>
  captureEvent('autocomplete_suggestion_clicked', { query_text: query, suggestion, rank });

// ===========================================================================
// PINNING / DOCUMENT ACTIONS
// ===========================================================================

export const trackPinDocument = (documentId, title, action) =>
  captureEvent('document_pinned', { document_id: documentId, title, action }); // action: 'pin' | 'unpin'

// ===========================================================================
// FILTERS
// ===========================================================================

export const trackFilterApplied = (filterName, filterValue, query) =>
  captureEvent('filter_applied', { filter_name: filterName, filter_value: filterValue, query_text: query });

export const trackFilterCleared = (query) =>
  captureEvent('filter_cleared', { query_text: query });

// ===========================================================================
// FOLLOW-UP QUESTIONS
// ===========================================================================

export const trackFollowUpQuestionClicked = (question, parentQuery) =>
  captureEvent('followup_question_clicked', { question, parent_query: parentQuery });

// ===========================================================================
// LEGACY ALIASES (keep backward-compat for existing call sites)
// ===========================================================================

export const trackSearch = (query) => trackQuerySubmitted(query);

export const trackFeedback = (feedbackType, documentId, query, reason = null) =>
  trackResponseRated(query, { rating: feedbackType === 'positive' ? 'positive' : 'negative', reason, documentId });

/** @deprecated use trackResponseRated. Kept for apiCall.js compatibility. */
export const trackQAQuery = (question, hasAnswer) =>
  trackResponseGenerated(question, { hasAnswer: Boolean(hasAnswer) });

/** @deprecated use trackCitationClicked. Kept for apiCall.js compatibility. */
export const trackFeedbackComment = (documentId, query) =>
  captureEvent('feedback_comment_submitted', { document_id: documentId, query_text: query });

export const trackAPIError = (endpoint, status, message) =>
  captureEvent('api_error', { endpoint, status, message });

export default posthog;
